// @flow
import React from 'react';
import cn from 'classnames';
// eslint-disable-next-line import/named
import { type IntlShape, FormattedMessage } from 'react-intl';

// Components
import { Container, Row, Col } from 'reactstrap';
import ButtonHeader from 'components/atoms/ButtonHeader';
import Modal from 'components/atoms/Modal';
import ModalServicesList from 'components/molecules/ModalServicesList';
import Price from 'components/atoms/Price';
import BookItButton from 'components/molecules/BookItButton';
import SidebarButton from 'components/pages/User/Sidebar/SidebarButton';

// Utils
import { isDiscountValid } from 'utils/promotions';

import './CTAFlowHeader.scss';

import { object } from '@storybook/addon-knobs';

const ns = 'cta-flow-header';
const modalNs = 'services-modal';

export type Props = {
  className: string,
  label: string,
  backButtonAriaLabel: string,
  labelCtaButton: string | null,
  routeTo: string,
  typeButton: string,
  hasPromotion: boolean,
  isFlexPayEnabled: boolean,
  showPrice: boolean,
  ctaButtonDisabled: boolean,
  hideServices: boolean,
  isBookItButtonVisible: boolean,
  goTo: void => void,
  gtmUserEventShoppingCart: void => void,
  onClick: void => void,
  onClickCtaButton: void => void,
  openModalBookItButton: () => void,
  throwErrorBookingBookItButton: () => void,
  intl: IntlShape,
  services: Array<Object>,
  servicesCount: number,
  totalPrice: object,
  topCTA: string | null,
  isLoggedIn: boolean,
  firstName: string,
  lastName: string,
  nextButtonAriaLabel: string,
  hideBackButton: boolean
};

class CTAFlowHeader extends React.Component<Props> {
  static defaultProps = {
    hideBackButton: false
  };

  constructor(props: Props) {
    super(props);
    (this: any).modal = React.createRef();
  }

  getFinancingOptionMessage() {
    const { isFlexPayEnabled } = this.props;

    // TODO: remove defaultMessage once localization is ready
    return (
      isFlexPayEnabled && (
        <Row>
          <Col className={`${modalNs}__financing-text`}>
            <FormattedMessage
              id="c8.review.financing.options"
              defaultMessage="We offer financing options, talk to your service advisor for details."
            >
              {text => text}
            </FormattedMessage>
          </Col>
        </Row>
      )
    );
  }

  getCtaButtonCss = () => {
    const { topCTA } = this.props;
    if (topCTA === 'squared') return 'cta-version-3-next-button';
    return 'cta-version-1-next-button';
  };

  getCtaServicesButtonCss = () => {
    const { servicesCount, topCTA } = this.props;

    if (servicesCount < 1) return 'cta-hide';
    if (topCTA === 'squared') return 'cta-version-3-services-button visible';

    return 'cta-version-1-services-button visible';
  };

  getCtaSidebarButtonCss = () => {
    const { topCTA } = this.props;
    if (topCTA === 'squared') {
      return 'cta-version-3-sidebar-button';
    }
    return 'cta-version-1-sidebar-button';
  };

  getCtaContainerCss = () => {
    const { className, topCTA } = this.props;
    if (topCTA === 'squared')
      return className ? `${className} cta-version-3` : 'cta-version-3';
    return className ? `${className} cta-version-1` : 'cta-version-1';
  };

  render() {
    const {
      label,
      routeTo,
      services,
      servicesCount,
      totalPrice,
      gtmUserEventShoppingCart,
      onClick,
      showPrice,
      hasPromotion,
      backButtonAriaLabel,
      ctaButtonDisabled,
      onClickCtaButton,
      hideServices,
      typeButton,
      openModalBookItButton,
      throwErrorBookingBookItButton,
      isBookItButtonVisible,
      labelCtaButton,
      isLoggedIn,
      firstName,
      lastName,
      nextButtonAriaLabel,
      hideBackButton
    } = this.props;
    const { discountedPrice, price } = totalPrice;
    const displayPrice = showPrice && price > 0;
    const ctaContainerClass = this.getCtaContainerCss();

    return (
      <Container className={cn(ctaContainerClass, ns)}>
        <Modal
          ref={(this: any).modal}
          title={() => {
            return (
              <React.Fragment>
                {displayPrice && (
                  <Row>
                    <Col xs={'6'}>
                      <FormattedMessage id="c8.cart.total.label">
                        {text => text}
                      </FormattedMessage>
                      :
                    </Col>
                    <Col
                      xs={'6'}
                      className={`${modalNs}__col ${modalNs}__col--single-line`}
                    >
                      {hasPromotion &&
                        isDiscountValid(price, discountedPrice) && (
                          <div
                            className={`${modalNs}__service-price ${modalNs}__service-price--promotion mr-2`}
                          >
                            <Price value={price} showZero={true} />
                          </div>
                        )}
                      <div className={`${modalNs}__service-price`}>
                        <Price
                          value={hasPromotion ? discountedPrice : price}
                          showZero={true}
                        />
                      </div>
                    </Col>
                  </Row>
                )}
                {this.getFinancingOptionMessage()}
              </React.Fragment>
            );
          }}
        >
          <ModalServicesList serviceList={services} />
        </Modal>
        <Row>
          <Col>
            {!hideBackButton && (
              <ButtonHeader
                ariaLabel={backButtonAriaLabel}
                label={label || ''}
                to={routeTo}
                onClick={onClick}
                type="cta-back"
              />
            )}
          </Col>
          <Col>
            <React.Fragment>
              {isLoggedIn && (
                <div className={this.getCtaSidebarButtonCss()}>
                  <SidebarButton firstName={firstName} lastName={lastName} />
                </div>
              )}
              {isBookItButtonVisible && (
                <BookItButton
                  openModal={openModalBookItButton}
                  throwErrorBooking={throwErrorBookingBookItButton}
                  isFromCTA={true}
                />
              )}
              {!isBookItButtonVisible && (
                <FormattedMessage id="c8.cta.header.next" defaultMessage="NEXT">
                  {nextLabel => (
                    <ButtonHeader
                      label={labelCtaButton || nextLabel}
                      type={typeButton}
                      onClick={onClickCtaButton}
                      className={this.getCtaButtonCss()}
                      disabled={ctaButtonDisabled}
                      ariaLabel={nextButtonAriaLabel}
                    />
                  )}
                </FormattedMessage>
              )}
              {!hideServices && (
                <FormattedMessage
                  id="c8.aria.number.services.selected"
                  values={{ servicesCount }}
                >
                  {text => (
                    <ButtonHeader
                      label={`${servicesCount}`}
                      type="cta-services"
                      onClick={() => {
                        gtmUserEventShoppingCart();
                        (this: any).modal.current.setShowModal(true);
                      }}
                      ariaLabel={text}
                      className={this.getCtaServicesButtonCss()}
                    />
                  )}
                </FormattedMessage>
              )}
            </React.Fragment>
          </Col>
        </Row>
      </Container>
    );
  }
}

export default CTAFlowHeader;



// WEBPACK FOOTER //
// ./src/components/molecules/CTAFlowHeader/CTAFlowHeader.js